import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import jwt_decode from "jwt-decode";

// Auth Views
import Login from "../views/Auth/LoginView.vue";
import Signup from "../views/Auth/Signup.vue";

// Main Views
import HomeView from "../views/HomeView.vue";
import Students from "../views/Students.vue";
import Attendance from "../views/Attendance.vue";
import MarkAttendance from "../views/NFC/MarkAttendance.vue";
import WEBDashboard from "../views/WEBDashboard.vue";
import Helpandsupport from "../views/Help&Support.vue";
import createSchool from "../views/School/createSchool.vue";
import schoolDetails from "../views/School/schoolDtails.vue";
import editSchool from "../views/School/editSchool.vue";
import Teacher from "../views/Teacher.vue";
import Profile from "../components/Profile.vue";
import TeacherAttendence from "../views/TeacherAttendence.vue";
import scanedTeacherAttendence from "../views/QrAttendence/scanedTeacherAttendence.vue";
import Class from "../views/Class.vue";

import page404 from "../views/404.vue";
import ForgotPassword from "../views/Auth/ForgotPassword.vue";
import LeaveRequest from "../views/LeaveRequests.vue";
import Onboarding from "../components/Onboarding.vue";
import AttendanceMethods from "../views/AttendanceMethods.vue";
import Features from "../views/features.vue";
import DeviceManager from "../views/DeviceManager.vue";
import report from "../views/Report.vue";
import sales from "../views/sales.vue";
import FeeManagement from "../views/FeeManagement.vue";

const routes = [
  // Public Routes
  { path: "/login", name: "Login", component: Login },
  { path: "/signup", name: "Signup", component: Signup },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
  },
  { path: "/404", name: "page404", component: page404 },

  // Protected Routes
  {
    path: "/",
    name: "Home",
    component: HomeView,
    meta: { requiresAuth: true },
  },
  {
    path: "/onboarding",
    name: "Onboarding",
    component: Onboarding,
    meta: { requiresAuth: true },
  },
  {
    path: "/students",
    name: "Students",
    component: Students,
    meta: { requiresAuth: true },
  },
  {
    path: "/attendance",
    name: "Attendance",
    component: Attendance,
    meta: { requiresAuth: true },
  },
  {
    path: "/mark-attendance/:studentId",
    name: "MarkAttendance",
    component: MarkAttendance,
    meta: { requiresAuth: true },
  },
  {
    path: "/WEBDashboard",
    name: "WEBDashboard",
    component: WEBDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/Helpandsupport",
    name: "Helpandsupport",
    component: Helpandsupport,
    meta: { requiresAuth: true },
  },
  {
    path: "/createSchool",
    name: "createSchool",
    component: createSchool,
    meta: { requiresAuth: true },
  },
  {
    path: "/schoolDetails",
    name: "schoolDetails",
    component: schoolDetails,
    meta: { requiresAuth: true },
  },
  {
    path: "/editSchool",
    name: "editSchool",
    component: editSchool,
    meta: { requiresAuth: true },
  },

  {
    path: "/teacher",
    name: "Teacher",
    component: Teacher,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true },
  },
  {
    path: "/teacherAttendance",
    name: "TeacherAttendance",
    component: TeacherAttendence,
    meta: { requiresAuth: true },
  },
  {
    path: "/scanedTeacherAttendence",
    name: "scanedTeacherAttendence",
    component: scanedTeacherAttendence,
    meta: { requiresAuth: true },
  },

  {
    path: "/classes",
    name: "Class",
    component: Class,
    meta: { requiresAuth: true },
  },
  {
    path: "/leaveRequest",
    name: "leaveRequest",
    component: LeaveRequest,
    meta: { requiresAuth: true },
  },

  {
    path: "/AttendanceMethods",
    name: "AttendanceMethods",
    component: AttendanceMethods,
    meta: { requiresAuth: true },
  },
  {
    path: "/features",
    name: "Features",
    component: Features,
    meta: { requiresAuth: true },
  },
  {
    path: "/devices",
    name: "DeviceManager",
    component: DeviceManager,
    meta: { requiresAuth: true },
  },
  {
    path: "/report",
    name: "report",
    component: report,
    meta: { requiresAuth: true },
  },
  {
    path: "/sales",
    name: "sales",
    component: sales,
    meta: { requiresAuth: true },
  },
  {
    path: "/fee-management",
    name: "FeeManagement",
    component: FeeManagement,
    meta: { requiresAuth: true, role: "admin" },
  },

  // Catch-all
  { path: "/:pathMatch(.*)*", redirect: "/404" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/* ---------------- JWT EXPIRY CHECK ---------------- */
function isTokenInvalid(token) {
  try {
    const decoded = jwt_decode(token);

    // no exp → invalid
    if (!decoded.exp) return true;

    // expired
    if (decoded.exp * 1000 < Date.now()) return true;

    return false;
  } catch (err) {
    // decode failed → token modified
    return true;
  }
}

/* ---------------- GLOBAL GUARD ---------------- */
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem("token");
  const isAuthenticated = !!token;

  // ⛔ logout immediately if token is expired
  if (token && isTokenInvalid(token)) {
    localStorage.removeItem("token");
    store.commit?.("LOGOUT");
    return next({ name: "Login" });
  }

  const publicRoutes = ["Login", "Signup", "ForgotPassword", "page404"];

  // Not logged in → go to Login
  if (!isAuthenticated && !publicRoutes.includes(to.name)) {
    return next({ name: "Login" });
  }

  // Logged in → block Login/Signup
  if (isAuthenticated && ["Login", "Signup"].includes(to.name)) {
    return next({ name: "Home" });
  }

  // Logged in → load app + onboarding rules
  if (isAuthenticated) {
    try {
      await store.dispatch("initializeApp");
    } catch (error) {
      // backend returned 401 / token bad
      localStorage.removeItem("token");
      store.commit?.("LOGOUT");
      return next({ name: "Login" });
    }

    // 🔒 Enforce role restriction (e.g. admin-only routes like Fee Management)
    if (to.meta?.role && store.state.userRole !== to.meta.role) {
      return next({ name: "Home" });
    }

    const classes = store.state.classes || [];
    const user = store.state.user || {};

    const validClasses = classes.filter(
      (cls) => cls.name?.toLowerCase() !== "passout",
    );

    const needsOnboarding = !user.schoolId || validClasses.length === 0;

    const onboardingRoutes = ["Onboarding", "createSchool", "Class"];

    if (needsOnboarding && !onboardingRoutes.includes(to.name)) {
      return next({ name: "Onboarding" });
    }
  }

  next();
});

export default router;
